import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineFileImage, AiOutlineFilePdf } from "react-icons/ai";
import { LiaFileUploadSolid } from "react-icons/lia";
import {
  ActionIcon,
  Alert,
  Button,
  ComboboxItem,
  Group,
  Loader,
  Modal,
  ScrollAreaAutosize,
  Select,
  Tabs,
  Text,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconCalendar, IconInfoCircle, IconTrash } from "@tabler/icons-react";

import { StyledEditModalContent } from "@pages/Patients/Laboratory/components/EditModal/EditModal.styled";

import { openDeleteModal } from "@components/DeleteModal";
import { ScrollArea } from "@components/ScrollArea";

import { handleOnChange } from "@utils/handleOnChange";
import { truncateWithEllipsis } from "@utils/truncateText";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";
import {
  MAX_FILENAME_LENGTH_AFTER_UPLOAD,
  MAX_FILENAME_LENGTH_BEFORE_UPLOAD,
  MEGABYTE,
  ROLES,
} from "@constants/index";

import { useDeleteImmunizationFile } from "@hooks/mutations/immunization/useDeleteImmunizationFile";
import { useUpdateImmunization } from "@hooks/mutations/immunization/useUpdateImmunization";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";

import { useAuth } from "@contexts/auth";

import { Immunization } from "@customTypes/immunization";


interface EditDialogProps {
  immunization: Partial<Immunization>;
  opened: boolean;
  close: () => void;
}

export const EditModal = ({ immunization, opened, close }: EditDialogProps) => {
  const { t } = useTranslation();
  const { token, id: userId, roles , can } = useAuth();

  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);
  const [immunizationDate, setImmunizationDate] = useState<Date | null>(
    immunization.immunizationDate ?? null
  );
  const [formData, setFormData] = useState(immunization);
  const [fileIndex, setFileIndex] = useState(-1);
  const [immunizationFiles, setImmunizationFiles] = useState<string[]>(
    immunization.files as string[]
  );
  const [doctor, setDoctor] = useState<ComboboxItem | null>(null);
  const [supportStaff, setSupportStaff] = useState<ComboboxItem | null>(null);

  const { mutate: updateImmunization, isLoading: isUpdateImmLoading } =
    useUpdateImmunization();
  const { mutate: deleteImmunizationFile, isLoading: isDeleteImmFileLoading } =
    useDeleteImmunizationFile();
  const {
    data: doctorsData,
    isLoading: isDoctorsLoading,
    isRefetching: isDoctorsRefetching,
  } = useGetUsers({ permission: "preventative.create" });
  const {
    data: supportStaffData,
    isLoading: isSupportStaffLoading,
    isRefetching: isSupportStaffRefetching,
  } = useGetUsers({ permission: "preventative.edit" });

  const doctorsComboboxItems = useMemo(() => {
    return doctorsData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [doctorsData]);
  const supportStaffComboboxItems = useMemo(() => {
    return supportStaffData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [supportStaffData]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const handleUpdateImmunization = () => {
    const jsFormData = new FormData();

    uploadedFiles.length &&
      uploadedFiles.forEach(file => {
        jsFormData.append(`files`, file);
      });

    jsFormData.append("medication_name", formData?.medication ?? "");
    jsFormData.append(
      "administered_by",
      doctor?.value === userId ? userId : doctor?.value ?? ""
    );
    jsFormData.append("aid", supportStaff?.value ?? "");
    jsFormData.append("date", (immunizationDate as Date).toISOString() ?? "");
    jsFormData.append("doses", formData?.doses ?? "");
    jsFormData.append("reason", formData?.reason ?? "");

    console.group("jsFormData");
    jsFormData.forEach((value, key) => {
      console.log(`${key}: ${value}`);
    });
    console.groupEnd();

    updateImmunization(
      {
        immunization: jsFormData,
        immunizationId: formData.id as string,
        patientId: formData.patientId as string,
      },
      {
        onSuccess: () => {
          close();
          setUploadedFiles([]);
        },
      }
    );
  };

  useEffect(() => {
    if (immunization) {
      setDoctor({
        label: immunization.administeredBy?.fullName as string,
        value: immunization.administeredBy?.id as string,
      });
      setSupportStaff({
        label: immunization.supportStaff?.fullName as string,
        value: immunization.supportStaff?.id as string,
      });
    }
  }, [immunization]);

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      title={<Text fw={500}>{t("edit-immunization")}</Text>}>
      <StyledEditModalContent>
        <Tabs defaultValue="info">
          <Tabs.List grow>
            <Tabs.Tab value="info">{t("info")}</Tabs.Tab>
            <Tabs.Tab value="previous-files">{t("previous-files")}</Tabs.Tab>
            <Tabs.Tab value="add-files">{t("add-files")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="info">
            <form className="form">
              <div className="form-field">
                <label htmlFor="medication">{t("medication")}</label>
                <input
                  type="text"
                  // placeholder="Eg: 2/2"
                  id="medication"
                  name="medication"
                  value={formData.medication}
                  onChange={e => handleOnChange(e, setFormData, formData)}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("doctor")}
                  placeholder={t("select-doctor")}
                  withAsterisk
                  disabled={!can("preventative.assign")}
                  data={doctorsComboboxItems || []}
                  value={doctor?.value ?? null}
                  onChange={(_val, option) => setDoctor(option)}
                  searchable
                  rightSection={
                    isDoctorsLoading || isDoctorsRefetching ? (
                      <Loader size={16} color="accent.4" />
                    ) : null
                  }
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("support-staff")}
                  placeholder={t("select-support-staff")}
                  data={supportStaffComboboxItems || []}
                  value={supportStaff?.value ?? null}
                  onChange={(_val, option) => setSupportStaff(option)}
                  rightSection={
                    isSupportStaffLoading || isSupportStaffRefetching ? (
                      <Loader size={16} color="accent.4" />
                    ) : null
                  }
                  searchable
                />
              </div>

              <div className="form-group">
                <div className="form-field">
                  <DatePickerInput
                    label={t("immunization-date")}
                    value={immunizationDate}
                    onChange={setImmunizationDate}
                    leftSection={<IconCalendar size={18} stroke={1.5} />}
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="doses">{t("dosage")}</label>
                  <input
                    type="text"
                    // placeholder="Eg: 2/2"
                    id="doses"
                    name="doses"
                    value={formData.doses}
                    onChange={e => handleOnChange(e, setFormData, formData)}
                  />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="reason">{t("reason")}</label>
                <textarea
                  rows={5}
                  placeholder={t("type-reason")}
                  id="reason"
                  name="reason"
                  value={formData.reason ?? ""}
                  onChange={e =>
                    handleOnChange(e, setFormData, formData)
                  }></textarea>
              </div>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="previous-files">
            <div className="files-wrapper">
              <div className="uploaded-files">
                <h4>{t("previously-uploaded-files")}</h4>
                <ScrollArea
                  scrollbarOrientation="vertical"
                  className={`previous-files ${
                    immunization.files?.length && immunization.files.length > 6
                      ? "show-gradient"
                      : ""
                  }`}>
                  {immunizationFiles?.length ? (
                    <ul>
                      {immunizationFiles.map((file, index) => (
                        <li key={index}>
                          <div className="content">
                            {(file as string).slice(-3) === "pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={"https://" + file + `?token=${token}`}>
                              {truncateWithEllipsis(
                                (file as string).split("/")[3],
                                MAX_FILENAME_LENGTH_AFTER_UPLOAD
                              )}
                            </a>
                          </div>

                          <Tooltip
                            label={t("delete-immunization-file")}
                            withArrow>
                            <PermissionGate permission="preventative.delete"><ActionIcon
                              color="red"
                              loading={
                                isDeleteImmFileLoading && fileIndex === index
                              }
                              variant="outline"
                              onClick={() => {
                                setFileIndex(index);
                                openDeleteModal({
                                  textNode: (
                                    <Text fz={"sm"}>
                                      {t("delete-lab-file-message")}
                                    </Text>
                                  ),
                                  onConfirm: () => {
                                    if (immunization?.id) {
                                      deleteImmunizationFile(
                                        {
                                          immunizationId: immunization.id,
                                          fileName: (file as string).split(
                                            "/"
                                          )[3],
                                          patientId:
                                            formData.patientId as string,
                                        },
                                        {
                                          onSuccess: () =>
                                            setImmunizationFiles(
                                              immunizationFiles.filter(
                                                (_, idx) => idx !== index
                                              )
                                            ),
                                        }
                                      );
                                    }
                                  },
                                });
                              }}>
                              <IconTrash size={16} />
                            </ActionIcon></PermissionGate>
                          </Tooltip>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="empty-label">
                      {t("empty-previously-uploaded-files")}
                    </p>
                  )}
                </ScrollArea>
              </div>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="add-files">
            <div className="files-wrapper">
              <div
                {...getRootProps({
                  className: `dropzone ${isDragActive ? "active" : ""} ${
                    isDragAccept ? "accept" : ""
                  } ${isDragReject ? "reject" : ""}`,
                })}>
                <input {...getInputProps()} />

                <div className="text-content">
                  <div className="svg-wrapper">
                    <LiaFileUploadSolid />
                  </div>
                  <div className="p-wrapper">
                    <p>{t("drag-file-here")}</p>
                    <p>{t("drag-file-here-continuation")}</p>
                  </div>
                </div>
              </div>
              <div className="uploaded-files">
                <h4>{t("uploaded-files")}</h4>
                {!uploadedFiles.length ? (
                  <Alert
                    mt={"md"}
                    variant="light"
                    color="blue"
                    icon={<IconInfoCircle />}>
                    <Text fz={14} lh={1.5} c="blue">
                      {t("files-appear-info")}
                    </Text>
                  </Alert>
                ) : (
                  <ScrollAreaAutosize h={200} type="auto">
                    <ul>
                      {uploadedFiles.map(file => (
                        <li key={file.name}>
                          <div className="content">
                            {file.type === "application/pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <p>
                              {truncateWithEllipsis(
                                file.name,
                                MAX_FILENAME_LENGTH_BEFORE_UPLOAD
                              )}{" "}
                              ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})
                            </p>
                          </div>

                          <ActionIcon
                            color="red"
                            variant="outline"
                            onClick={() => removeFile(file)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </li>
                      ))}
                    </ul>
                  </ScrollAreaAutosize>
                )}
              </div>
            </div>
          </Tabs.Panel>
        </Tabs>

        <Group justify="end" mt="xl" pb="md">
          <PermissionGate permission="preventative.edit"><Button
            onClick={handleUpdateImmunization}
            loading={isUpdateImmLoading}>
            {t("update-immunization")}
          </Button></PermissionGate>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
